import React from "react";
import { Helmet } from "react-helmet-async";

import { useLang, localizedPath, stripLangPrefix } from "../../lib/i18n";

const SITE_URL = "https://www.laviens.com";
const DEFAULT_OG = `${SITE_URL}/og-cover.jpg`;
const SITE_NAME = "Laviens — Studio Olistico";

export interface IBreadcrumb {
  name: string;
  path: string; // absolute path, e.g. "/treatments"
}

export interface ISeoHeadProps {
  /** Full title shown in tab + SERP; keep <= 60 chars when possible */
  title: string;
  /** Meta description; 140–160 chars ideal */
  description: string;
  /** Absolute path of the current page, e.g. "/treatments" */
  path: string;
  /** Topic keywords — used by Bing, Yandex, some AI bots */
  keywords?: string;
  /** OpenGraph image. Defaults to site-wide cover. */
  image?: string;
  /** og:type — "website", "article", "profile" */
  ogType?: "website" | "article" | "profile";
  /** Optional breadcrumb trail (emits BreadcrumbList JSON-LD) */
  breadcrumbs?: IBreadcrumb[];
  /** Optional extra JSON-LD blocks (Service, Article, Speakable, etc.) */
  jsonLd?: unknown[] | unknown;
  /** Mark portions of the page as voice-speakable (Google Assistant / Alexa) */
  speakableCssSelectors?: string[];
  /** Date published (ISO) — for article/about pages */
  datePublished?: string;
  /** Date modified (ISO) */
  dateModified?: string;
  /** Article author name (default: Laviens) */
  author?: string;
  /** Allow indexing; default true */
  index?: boolean;
}

const toAbsolute = (path: string): string => {
  if (!path) return SITE_URL + "/";
  if (path.startsWith("http")) return path;
  return SITE_URL + (path.startsWith("/") ? path : `/${path}`);
};

/**
 * Single source of truth for per-page SEO metadata.
 * Emits every meta/link tag a modern crawler (Google, Bing, Yandex)
 * and AI crawler (GPTBot, ClaudeBot, PerplexityBot, Google-Extended,
 * Applebot-Extended) looks for, plus structured data.
 */
const SeoHead: React.FC<ISeoHeadProps> = ({
  title,
  description,
  path,
  keywords,
  image = DEFAULT_OG,
  ogType = "website",
  breadcrumbs,
  jsonLd,
  speakableCssSelectors,
  datePublished,
  dateModified,
  author = "Laviens — Studio Olistico",
  index = true,
}) => {
  const { lang } = useLang();
  const canonicalUrl = toAbsolute(path);
  const absImage = image.startsWith("http") ? image : toAbsolute(image);

  // Build hreflang alternates from the canonical (Italian, no prefix) path.
  const itPath = stripLangPrefix(path);
  const itUrl = toAbsolute(localizedPath(itPath, "it"));
  const enUrl = toAbsolute(localizedPath(itPath, "en"));

  const ogLocale = lang === "en" ? "en_US" : "it_IT";
  const htmlLang = lang === "en" ? "en" : "it-IT";

  const robots = index
    ? "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
    : "noindex, follow";

  const schemas: unknown[] = [];

  // BreadcrumbList
  if (breadcrumbs && breadcrumbs.length > 0) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: breadcrumbs.map((b, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: b.name,
        item: toAbsolute(b.path),
      })),
    });
  }

  // WebPage with speakable
  const webPageSchema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${canonicalUrl}#webpage`,
    url: canonicalUrl,
    name: title,
    description,
    inLanguage: htmlLang,
    isPartOf: { "@id": `${SITE_URL}/#website` },
    about: { "@id": `${SITE_URL}/#organization` },
    primaryImageOfPage: { "@type": "ImageObject", url: absImage },
    datePublished: datePublished || "2020-02-14T00:00:00+01:00",
    dateModified: dateModified || new Date().toISOString(),
  };
  if (speakableCssSelectors && speakableCssSelectors.length > 0) {
    webPageSchema.speakable = {
      "@type": "SpeakableSpecification",
      cssSelector: speakableCssSelectors,
    };
  }
  schemas.push(webPageSchema);

  // Extra user-supplied schemas
  if (jsonLd) {
    if (Array.isArray(jsonLd)) schemas.push(...jsonLd);
    else schemas.push(jsonLd);
  }

  return (
    <Helmet prioritizeSeoTags>
      <html lang={htmlLang} />
      {/* Core */}
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="author" content={author} />
      <meta name="robots" content={robots} />
      <meta name="googlebot" content={robots} />
      <meta name="bingbot" content={robots} />
      <link rel="canonical" href={canonicalUrl} />

      {/* hreflang — bilingual: IT default, EN alternate */}
      <link rel="alternate" href={itUrl} hrefLang="it" />
      <link rel="alternate" href={itUrl} hrefLang="it-IT" />
      <link rel="alternate" href={enUrl} hrefLang="en" />
      <link rel="alternate" href={enUrl} hrefLang="en-US" />
      <link rel="alternate" href={itUrl} hrefLang="x-default" />

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={ogLocale} />
      <meta
        property="og:locale:alternate"
        content={lang === "en" ? "it_IT" : "en_US"}
      />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={absImage} />
      <meta property="og:image:secure_url" content={absImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title} />

      {/* Twitter / X */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={absImage} />
      <meta name="twitter:image:alt" content={title} />

      {/* Article dates (used by Google, Bing, LLM citation engines) */}
      {datePublished && (
        <meta property="article:published_time" content={datePublished} />
      )}
      {dateModified && (
        <meta property="article:modified_time" content={dateModified} />
      )}

      {/* Structured data — one script per graph for cleaner parsing */}
      {schemas.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default SeoHead;
